import { Dispatch, SetStateAction, useCallback, useEffect, useSyncExternalStore } from "react";

function getSessionStorageItem(key: string) {
  return window.sessionStorage.getItem(key);
}

function removeSessionStorageItem(key: string) {
  window.sessionStorage.removeItem(key);
  dispatchStorageEvent(key, null);
}

function setSessionStorageItem(key: string, value: any) {
  const stringifiedValue = JSON.stringify(value);
  window.sessionStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
}

function dispatchStorageEvent(key: string, newValue: string) {
  window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}

function useSessionStorageSubscribe(callback: () => void) {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}

const getSessionStorageServerSnapshot = () => {
  throw Error("useSessionStorage is a client-only hook");
};

/**
 * Custom hook that uses sessionStorage as a state management solution.
 * It provides a way to store and retrieve serializable data in sessionStorage.
 *
 * **Note:** The setter function returned does not have a stable identity like
 * the one returned by `useState`. This means you need to add it to the deps
 * array of your component if you want to use it in a `useEffect` hook, else,
 * it won't use the latest value if you pass a function to it.
 *
 * @param key - The key to use when storing and retrieving data.
 * @param initialValue - The initial value to use when the key is not found in sessionStorage.
 * @returns A tuple containing the current value and a function to update it.
 */
export function useSessionStorage<T>(key: string, initialValue?: T): [T, Dispatch<SetStateAction<T>>] {
  const getSnapshot = () => getSessionStorageItem(key);

  const store = useSyncExternalStore(useSessionStorageSubscribe, getSnapshot, getSessionStorageServerSnapshot);

  const setState = useCallback(
    (v: SetStateAction<T>) => {
      try {
        const nextState = typeof v === "function" ? (v as (prevState: T) => T)(store ? JSON.parse(store) : initialValue) : v;

        if (nextState === undefined || nextState === null) {
          removeSessionStorageItem(key);
        } else {
          setSessionStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store]
  );

  useEffect(() => {
    if (getSessionStorageItem(key) === null && typeof initialValue !== "undefined") {
      setSessionStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  return [store ? JSON.parse(store) : initialValue, setState];
}
